import React, { useState } from 'react';
import { Form, Row, Col, Button, Alert, Spinner } from 'react-bootstrap';
import { HiDocumentReport } from "react-icons/hi";
import PayrollLayout from '../../Components/PayrollLayout.jsx';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { format, parseISO } from 'date-fns';
import './reports.css'
export default function PayrollReports() {
  const [generateStartDate, setGenerateStartDate] = useState('');
  const [generateEndDate, setGenerateEndDate] = useState('');
  const [fetchStartDate, setFetchStartDate] = useState('');
  const [fetchEndDate, setFetchEndDate] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [showTable, setShowTable] = useState(false);
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('generate'); // 'generate' or 'fetch'
  const GENERATE_API_URL = 'https://backend.ems.hiradentallab.com/api/attendance/create';
  const FETCH_API_URL = 'https://backend.ems.hiradentallab.com/api/attendance/monthly-summary';
  const fetchData = async (start, end) => {
    try {
      setIsFetching(true);
      setServerError(null);
      
      const response = await fetch(`${FETCH_API_URL}?startDate=${start}&endDate=${end}`);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch data');
      }
      
      const result = await response.json();
      
      if (result?.data && Array.isArray(result.data)) {
        const formattedData = result.data.flatMap(summary => 
          summary.summary.map(item => ({
            userId: item.userId?.toString() || 'N/A',
            userName: item.userName || 'Unknown',
            totalLateMinutes: item.totalLateMinutes || 0,
            totalEarlyCloseMinutes: item.totalEarlyCloseMinutes || 0,
            totalExtraMinutes: item.totalExtraMinutes || 0,
            createdAt: summary.createdAt ? format(parseISO(summary.createdAt), 'yyyy-MM-dd HH:mm') : 'N/A'
          }))
        );
        setData(formattedData);
      } else {
        setData([]);
        setServerError('No summary data found in response');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      setServerError(error.message || 'Failed to fetch payroll data');
      setData([]);
    } finally {
      setIsFetching(false);
    }
  };

  const generateReport = async () => {
    try {
      setIsGenerating(true);
      setServerError(null);
      
      const response = await fetch(GENERATE_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          startDate: generateStartDate,
          endDate: generateEndDate
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to generate report');
      }
      
      const result = await response.json();
      
      if (result?.data?.summary && Array.isArray(result.data.summary)) {
        const formattedData = result.data.summary.map(item => ({
          userId: item.userId?.toString() || 'N/A',
          userName: item.userName || 'Unknown',
          totalLateMinutes: item.totalLateMinutes || 0,
          totalEarlyCloseMinutes: item.totalEarlyCloseMinutes || 0,
          totalExtraMinutes: item.totalExtraMinutes || 0,
          createdAt: result.data.createdAt ? format(parseISO(result.data.createdAt), 'yyyy-MM-dd HH:mm') : 'N/A'
        }));
        
        setData(formattedData);
        setShowTable(true);
      } else {
        setData([]);
        setServerError('No summary data found in response');
      }
    } catch (error) {
      console.error('Generate error:', error);
      setServerError(error.message || 'Failed to generate payroll report');
      setData([]);
    } finally {
      setIsGenerating(false);
    }
  };
  const validateGenerateForm = () => {
    const errors = {};
    if (!generateStartDate) errors.generateStartDate = 'Start date is required';
    if (!generateEndDate) errors.generateEndDate = 'End date is required';
    if (generateStartDate && generateEndDate && new Date(generateStartDate) > new Date(generateEndDate)) {
      errors.generateDateRange = 'Start date must be before end date';
    }
    return errors;
  };
  const validateFetchForm = () => {
    const errors = {};
    if (!fetchStartDate) errors.fetchStartDate = 'Start date is required';
    if (!fetchEndDate) errors.fetchEndDate = 'End date is required';
    if (fetchStartDate && fetchEndDate && new Date(fetchStartDate) > new Date(fetchEndDate)) {
      errors.fetchDateRange = 'Start date must be before end date';
    }
    return errors;
  };
  const handleGenerateReport = (e) => {
    e.preventDefault();
    const errors = validateGenerateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    generateReport();
  };
  const handleFetchReport = (e) => {
    e.preventDefault();
    const errors = validateFetchForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    fetchData(fetchStartDate, fetchEndDate);
    setShowTable(true);
  };
  const exportToExcel = () => {
    if (data.length === 0) return;
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'PayrollSummary');
    XLSX.writeFile(workbook, `payroll_summary_${new Date().toISOString().slice(0,10)}.xlsx`);
  };
  const exportToPDF = () => {
    if (data.length === 0) return;
    const doc = new jsPDF();
    doc.text(`Payroll Summary Report`, 14, 16);
    doc.autoTable({
      head: [
        ['Employee ID', 'Name', 'Late (min)', 'Early Close (min)', 'Extra (min)']
      ],
      body: filteredData.map(item => [
        item.userId,
        item.userName,
        item.totalLateMinutes,
        item.totalEarlyCloseMinutes,
        item.totalExtraMinutes
      ]),
      margin: { top: 20 },
      styles: { fontSize: 9 },
      headStyles: { fillColor: [22, 160, 133] }
    });
    doc.save(`payroll_summary_${new Date().toISOString().slice(0,10)}.pdf`);
  };
  // Filter data based on search term
  const filteredData = data.filter(item => 
    item.userId.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.userName.toLowerCase().includes(searchTerm.toLowerCase())
  );
  // Pagination logic
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const currentData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };
  const pageRange = () => {
    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };
  return (
    <PayrollLayout>
    
      <div className="mb-4">
        <div className="d-flex mb-3" style={{marginTop:'20px'}}>
          <Button
            variant={activeTab === 'generate' ? 'primary' : 'outline-primary'}
            onClick={() => setActiveTab('generate')}
            className="me-2">
            Generate New Report
          </Button>
          <Button
            variant={activeTab === 'fetch' ? 'primary' : 'outline-primary'}
            onClick={() => setActiveTab('fetch')}>
            Fetch Existing Report
          </Button>
        </div>
        {activeTab === 'generate' ? (
          <div>
              <h1 className='reports-title'>
        <HiDocumentReport className="me-2" />
        Monthly Generate Summary Report
      </h1>
      <hr />
          <Form onSubmit={handleGenerateReport} className='reports-form mb-4'>
            <Row>
              <Col md={5} xs={12}>
                <Form.Group controlId="generateStartDate" className="mb-3">
                  <Form.Label>Start Date <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="date"
                    value={generateStartDate}
                    onChange={(e) => setGenerateStartDate(e.target.value)}
                    isInvalid={!!formErrors.generateStartDate}
                    max={generateEndDate || undefined}/>
                  <Form.Control.Feedback type="invalid">
                    {formErrors.generateStartDate}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={5} xs={12}>
                <Form.Group controlId="generateEndDate" className="mb-3">
                  <Form.Label>End Date <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="date"
                    value={generateEndDate}
                    onChange={(e) => setGenerateEndDate(e.target.value)}
                    isInvalid={!!formErrors.generateEndDate}
                    min={generateStartDate || undefined}/>
                  <Form.Control.Feedback type="invalid">
                    {formErrors.generateEndDate}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={2} xs={12} className="d-flex align-items-end mb-3">
                <Button
                  variant="primary"
                  type="submit"
                  disabled={isGenerating}
                  className="w-100">
                  {isGenerating ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"/>
                      Generating...
                    </>
                  ) : (
                    'Generate Report'
                  )}
                </Button>
              </Col>
            </Row>
            {formErrors.generateDateRange && (
              <Alert variant="danger" className="mt-2">
                {formErrors.generateDateRange}
              </Alert>)}
          </Form>
          </div>
        ) : (
          <div>
          <h1 className='reports-title'>
    <HiDocumentReport className="me-2" />
    Monthly Fetch Summary Report
    </h1>
  <hr />
          <Form onSubmit={handleFetchReport} className='reports-form mb-4'>
            <Row>
              <Col md={5} xs={12}>
                <Form.Group controlId="fetchStartDate" className="mb-3">
                  <Form.Label>Start Date <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="date"
                    value={fetchStartDate}
                    onChange={(e) => setFetchStartDate(e.target.value)}
                    isInvalid={!!formErrors.fetchStartDate}
                    max={fetchEndDate || undefined}/>
                  <Form.Control.Feedback type="invalid">
                    {formErrors.fetchStartDate}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={5} xs={12}>
                <Form.Group controlId="fetchEndDate" className="mb-3">
                  <Form.Label>End Date <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="date"
                    value={fetchEndDate}
                    onChange={(e) => setFetchEndDate(e.target.value)}
                    isInvalid={!!formErrors.fetchEndDate}
                    min={fetchStartDate || undefined}/>
                  <Form.Control.Feedback type="invalid">
                    {formErrors.fetchEndDate}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={2} xs={12} className="d-flex align-items-end mb-3">
                <Button
                  variant="primary"
                  type="submit"
                  disabled={isFetching}
                  className="w-100">
                  {isFetching ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"/>
                      Fetching...
                    </>
                  ) : (
                    'Fetch Report')}
                </Button>
              </Col>
            </Row>
            {formErrors.fetchDateRange && (
              <Alert variant="danger" className="mt-2">
                {formErrors.fetchDateRange}
              </Alert>
            )}
          </Form>
          </div>
        )}
      </div>
      {serverError && (
        <Alert variant="danger" className="mt-3">
          {serverError}
        </Alert>
      )}
      {showTable && (
        <div className="reports-table-container">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="d-flex gap-2">
              <Button
                variant="outline-success"
                onClick={exportToExcel}
                disabled={data.length === 0}>
                Export to Excel
              </Button>
              <Button
                variant="outline-danger"
                onClick={exportToPDF}
                disabled={data.length === 0}>
                Export to PDF
              </Button>
            </div>
            <Form.Group className="mb-3" style={{ width: '300px' }}>
              <Form.Control
                type="search"
                placeholder="Search employees..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);}}/>
            </Form.Group>
          </div>
          {filteredData.length === 0 ? (
            <div className="text-center py-5">
              <h5>No payroll data found</h5>
              <p className="text-muted">
                {searchTerm ? 'Try a different search term' : 'No records available for the selected date range'}
              </p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className='table table-striped'>
                  <thead className='reports-table-header'>
                    <tr>
                      <th>User ID</th>
                      <th>Name</th>
                      <th className="text-center">Late Absent Minutes</th>
                      <th className="text-center">Early Absent Minutes</th>
                      <th className="text-center">Overtime</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentData.map((item, index) => (
                      <tr key={`${item.userId}-${index}`}>
                        <td>{item.userId}</td>
                        <td>{item.userName}</td>
                        <td className="text-center">{item.totalLateMinutes.toLocaleString()}</td>
                        <td className="text-center">{item.totalEarlyCloseMinutes.toLocaleString()}</td>
                        <td className="text-center">{item.totalExtraMinutes.toLocaleString()}</td>
                      </tr>))}
                  </tbody>
                  <tfoot className='table-footer'>
                    <tr className='reports-table-footer'>
                      <td colSpan="2">Total Records</td>
                      <td className="text-center">
                        {filteredData.reduce((sum, item) => sum + item.totalLateMinutes, 0).toLocaleString()}
                      </td>
                      <td className="text-center">
                        {filteredData.reduce((sum, item) => sum + item.totalEarlyCloseMinutes, 0).toLocaleString()}
                      </td>
                      <td className="text-center">
                        {filteredData.reduce((sum, item) => sum + item.totalExtraMinutes, 0).toLocaleString()}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <div className="entries-pagination-container">
                <p className="showing-entries">
                  Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                  {Math.min(currentPage * itemsPerPage, filteredData.length)} of{" "}
                  {filteredData.length} entries
                </p>
                <nav className="pagination-container">
                  <ul className="pagination">
                    <li
                      className={`page-item ${
                        currentPage === 1 ? "disabled" : ""}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(currentPage - 1)}>
                        Previous
                      </button>
                    </li>
                    {pageRange().map((page) => (
                      <li
                        key={page}
                        className={`page-item ${
                          currentPage === page ? "active" : ""}`}>
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(page)}>
                          {page}
                        </button>
                      </li>))}
                    <li
                      className={`page-item ${
                        currentPage === totalPages ? "disabled" : ""}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(currentPage + 1)}>
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </>)}
        </div>)}
    </PayrollLayout>
  );
}